import { FC, useEffect } from 'react'
import { Box, Button, CircularProgress } from '@perk-ui/core'
import { LogOut } from 'react-feather'
import { useHistory, useLocation } from 'react-router-dom'

import SideBannerContainer from '../../components/SideBannerContainer'
import { useSitesContext } from '../../components/SitesProvider'
import { useAuth } from '../../features/auth/AuthContext'
import { User } from '../../features/query-hooks/useCurrentUser'
import { Site } from '../../features/query-hooks/useSitesAvailable'
import ProviderBanner from './components/ProviderBanner'
import ContactSupport from './steps/ContactSupport'
import CreateNewClinic from './steps/CreateNewClinic'
import PendingApproval from './steps/PendingApproval'
import ProviderComplete from './steps/ProviderComplete'
import SearchExistingClinic from './steps/SearchExistingClinic'

interface SitesQueryType {
  isLoading: boolean
  data?: Site[]
}

/**
 * Determines which provider step/state to show based on current user situation
 */
function useProviderState(
  user: User | undefined,
  sitesQuery: SitesQueryType,
  location: { search: string },
) {
  // Check for non-provider staff flag from AppErrorBoundary redirect
  const urlParams = new URLSearchParams(location.search)
  const isNonProvider = urlParams.get('staff_role') === 'non_provider'

  // Priority 0: Non-provider staff - show contact support
  if (isNonProvider) {
    return 'contact-support'
  }

  // Still loading user or sites data
  if (!user || sitesQuery.isLoading) {
    return 'loading'
  }

  const isActivated = user?.staff?.activated === true
  const hasActiveSites = user?.staff?.hasActiveSiteStaff ?? false
  const hasAnySites = (sitesQuery.data?.length ?? 0) > 0

  // Check localStorage flag for clinic creation flow
  const isCreatingClinic = localStorage.getItem('isCreatingClinic') === 'true'

  // Priority 1: Active clinic creation flow
  if (isCreatingClinic) {
    return 'create-clinic'
  }

  // Priority 2: User has active sites and is activated - all good!
  const userHasAccess = hasActiveSites && isActivated
  if (userHasAccess) {
    return 'complete'
  }

  // Priority 3: User has sites but not activated (pending approval)
  if (hasAnySites && !isActivated) {
    return 'pending-approval'
  }

  // Priority 4: Everything else - start/restart the clinic flow
  return 'search-clinic'
}

const Provider: FC = () => {
  const { user, signOut } = useAuth()
  const { sitesQuery } = useSitesContext()
  const history = useHistory()
  const location = useLocation()

  const providerState = useProviderState(user, sitesQuery, location)

  // Keep URL in sync with Provider state (optional - for cleaner URLs)
  useEffect(() => {
    if (providerState !== 'loading') {
      const expectedUrl = `/provider`
      if (location.pathname !== expectedUrl) {
        history.replace(expectedUrl)
      }
    }
  }, [providerState, location.pathname, history])

  // Show loading state
  if (providerState === 'loading') {
    return (
      <SideBannerContainer bannerSide="left" banner={<ProviderBanner />}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
          width="100%"
        >
          <CircularProgress size={40} />
        </Box>
      </SideBannerContainer>
    )
  }

  // Render appropriate step based on provider state
  const renderProviderStep = () => {
    switch (providerState) {
      case 'contact-support':
        return <ContactSupport />
      case 'search-clinic':
        return <SearchExistingClinic />
      case 'create-clinic':
        return <CreateNewClinic />
      case 'pending-approval':
        return <PendingApproval />
      case 'complete':
        return <ProviderComplete />
      default:
        return <SearchExistingClinic /> // fallback
    }
  }

  return (
    <SideBannerContainer
      bannerSide="left"
      banner={<ProviderBanner />}
      bannerWidth={5}
    >
      <Button
        variant="text"
        startIcon={<LogOut size={16} />}
        onClick={signOut}
        style={{
          position: 'absolute',
          top: '16px',
          right: '2.5em',
          zIndex: 1000,
          color: '#6B7280',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
        }}
      >
        Sign Out
      </Button>
      {renderProviderStep()}
    </SideBannerContainer>
  )
}

export default Provider
